// src/pages/PatientRegisterPage.jsx
// Ajustes: normaliza email en onChange/submit, muestra mensaje específico de email duplicado
import React, { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Avatar, Button, TextField, Link, Grid, Box, Typography, CssBaseline,
  CircularProgress, Fade, Divider, InputAdornment, Card, CardContent,
  MenuItem, Select, FormControl, InputLabel, FormHelperText
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CakeIcon from '@mui/icons-material/Cake';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import backgroundImage from '../assets/login.jpeg';
import CountryCodeSelect from '../components/CountryCodeSelect';

const PatientRegisterPage = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    date_of_birth: '',
    sexo: 'Masculino',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('+504');
  const navigate = useNavigate();

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : '#fff',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputBase-input': { color: theme.palette.text.primary },
    '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // normaliza email en tiempo real
    const nextValue = name === 'email' ? value.trim() : value;
    setFormData(prev => {
      const next = { ...prev, [name]: nextValue };
      if (name === 'email' && !prev.username) {
        const local = nextValue.split('@')[0] || '';
        next.username = local.replace(/[^a-zA-Z0-9._-]/g, '').toLowerCase();
      }
      return next;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const composePhone = (code, number) => {
    const trimmed = (number || '').trim();
    if (trimmed.startsWith('+')) return trimmed;
    return `${code} ${trimmed}`.replace(/\s+/g, ' ').trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.password2) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);

    const payload = {
      username: (formData.username || '').trim(),
      email: (formData.email || '').trim().toLowerCase(),
      first_name: (formData.first_name || '').trim(),
      last_name: (formData.last_name || '').trim(),
      phone_number: composePhone(countryCode, formData.phone_number),
      date_of_birth: formData.date_of_birth,
      sexo: formData.sexo,
      password: formData.password,
      password2: formData.password2,
    };

    try {
      await axiosInstance.post('/patients/register/', payload);
      toast.success('¡Registro exitoso! Por favor, inicia sesión.');
      navigate('/patient/login');
    } catch (err) {
      const serverErrors = err.response?.data || {};
      setErrors(serverErrors);
      if (serverErrors.email?.length) {
        toast.error(serverErrors.email[0]); // p.ej.: "Este email ya está en uso."
      } else if (serverErrors.username?.length) {
        toast.error(serverErrors.username[0]);
      } else if (serverErrors.password2?.length) {
        toast.error(serverErrors.password2[0]);
      } else {
        toast.error('Hubo un error en el registro. Revisa los campos.');
      }
      console.error('Error en registro:', serverErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{
      height: '100vh', width: '100vw', backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }}>
      <CssBaseline />
      <Fade in timeout={1000}>
        <Card elevation={8} sx={{
          width: { xs: '90%', sm: '520px' }, borderRadius: '28px',
          backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.92) : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)', boxShadow: theme.shadows[8],
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.9), width: 56, height: 56 }}>
                <PersonAddIcon fontSize="large" />
              </Avatar>
              <Box sx={{ ml: 2 }}>
                <Typography component="h1" variant="h5" fontWeight="bold" color="primary.main">
                  Registro de Paciente
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Crea tu cuenta para agendar citas
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box component="form" noValidate onSubmit={handleSubmit}>
              {/* Información Personal */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20, p: 0.5, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">Información Personal</Typography>
                </Box>

                <TextField name="first_name" label="Nombre *" value={formData.first_name} onChange={handleChange} autoComplete="given-name"
                  error={!!errors.first_name} helperText={errors.first_name?.[0] || ' '} required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon color="action" fontSize="small" /></InputAdornment>) }}
                  sx={{ ...inputSx, mb: 1 }} />

                <TextField name="last_name" label="Apellido *" value={formData.last_name} onChange={handleChange} autoComplete="family-name"
                  error={!!errors.last_name} helperText={errors.last_name?.[0] || ' '} required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon color="action" fontSize="small" /></InputAdornment>) }}
                  sx={{ ...inputSx, mb: 1 }} />

                <TextField name="date_of_birth" label="Fecha de Nacimiento *" type="date"
                  value={formData.date_of_birth} onChange={handleChange} autoComplete="bday"
                  error={!!errors.date_of_birth} helperText={errors.date_of_birth?.[0] || ' '}
                  required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><CakeIcon color="action" fontSize="small" /></InputAdornment>) }}
                  InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 1 }} />

                <FormControl fullWidth margin="normal" error={!!errors.sexo} sx={inputSx}>
                  <InputLabel id="sexo-label">Sexo *</InputLabel>
                  <Select labelId="sexo-label" name="sexo" value={formData.sexo} label="Sexo *" onChange={handleChange}> autoComplete="off"
                    <MenuItem value="Masculino">Masculino</MenuItem>
                    <MenuItem value="Femenino">Femenino</MenuItem>
                  </Select>
                  {errors.sexo && <FormHelperText>{errors.sexo[0]}</FormHelperText>}
                </FormControl>
              </Box>

              {/* Información de Contacto */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20, p: 0.5, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">Información de Contacto</Typography>
                </Box>

                <TextField name="email" label="Correo Electrónico *" type="email" placeholder="ejemplo@correo.com"
                  value={formData.email} onChange={handleChange} autoComplete="email"
                  error={!!errors.email} helperText={errors.email?.[0] || ' '}
                  required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon color="action" fontSize="small" /></InputAdornment>) }}
                  sx={{ ...inputSx, mb: 1 }} />

                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={5}>
                    <CountryCodeSelect value={countryCode} onChange={setCountryCode} textFieldProps={{ autoComplete: 'tel-country-code' }} sx={inputSx} />
                  </Grid>
                  <Grid item xs={7}>
                    <TextField name="phone_number" label="Teléfono *" placeholder="1234 5678"
                      value={formData.phone_number} onChange={handleChange}  autoComplete="tel-national"
                      error={!!errors.phone_number} helperText={errors.phone_number?.[0] || ' '}
                      required fullWidth margin="normal"
                      InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneIcon color="action" fontSize="small" /></InputAdornment>) }}
                      sx={{ ...inputSx }} />
                  </Grid>
                </Grid>
              </Box>

              {/* Información de Usuario */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20, p: 0.5, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">Información de Usuario</Typography>
                </Box>

                <TextField name="username" label="Nombre de Usuario *" placeholder="Usuario"
                  value={formData.username} onChange={handleChange} autoComplete="username"
                  error={!!errors.username} helperText={errors.username?.[0] || ' '}
                  required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon color="action" fontSize="small" /></InputAdornment>) }}
                  sx={{ ...inputSx }} />
              </Box>

              {/* Seguridad */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LockIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20, p: 0.5, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">Seguridad</Typography>
                </Box>

                <TextField name="password" label="Contraseña *" type="password"
                  value={formData.password} onChange={handleChange} autoComplete="new-password"
                  error={!!errors.password} helperText={errors.password?.[0] || ' '}
                  required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><VpnKeyIcon color="action" fontSize="small" /></InputAdornment>) }}
                  sx={{ ...inputSx, mb: 1 }} />

                <TextField name="password2" label="Confirmar Contraseña *" type="password"
                  value={formData.password2} onChange={handleChange} autoComplete="new-password"
                  error={!!errors.password2} helperText={errors.password2?.[0] || ' '}
                  required fullWidth margin="normal"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><VpnKeyIcon color="action" fontSize="small" /></InputAdornment>) }}
                  sx={{ ...inputSx }} />
              </Box>

              <Button type="submit" fullWidth variant="contained" disabled={loading}
                sx={{ mt: 2, mb: 3, py: 1.5, borderRadius: '20px', boxShadow: 'none', fontWeight: 'bold', fontSize: '1rem',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(25,118,210,0.3)' } }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Registrarse'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button component={RouterLink} to="/patient/login" startIcon={<ArrowBackIcon />} variant="tonal"
                  sx={{ color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: '20px', '&:hover': { transform: 'translateX(-5px)', backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}>
                  ¿Ya tienes una cuenta? Inicia sesión
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );
};

export default PatientRegisterPage;
